import { MenuItem, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface Props<T extends string | number | boolean> {
  id: string;
  label: string;
  value: unknown;
  options: readonly T[];
  disabled?: boolean;
  unsupportedReason?: string;
  fallbackHint?: string;
  /** Explains a configured value outside the presets; defaults to the JSON-managed hint. */
  customHint?: string;
  onChange: (value: T | undefined) => void;
}

export const VoicePresetSelect = <T extends string | number | boolean>({
  id,
  label,
  value,
  options,
  disabled,
  unsupportedReason,
  fallbackHint,
  customHint,
  onChange,
}: Props<T>) => {
  const { t } = useTranslation();
  const index = options.findIndex((option) => option === value);
  const configured = value !== undefined && index < 0;
  const jsonManaged = customHint ?? t('superAdminManageVoice.advanced.jsonManaged');
  const scalar = ['string', 'number', 'boolean'].includes(typeof value);
  const configuredLabel = scalar ? `${String(value)} — ${jsonManaged}` : jsonManaged;

  return (
    <TextField
      id={id}
      select
      fullWidth
      margin="dense"
      label={label}
      disabled={disabled || Boolean(unsupportedReason)}
      value={index >= 0 ? String(index) : configured ? 'json' : ''}
      helperText={
        unsupportedReason ??
        (configured ? jsonManaged : (fallbackHint ?? t('superAdminManageVoice.advanced.optional')))
      }
      onChange={(event) => {
        const selected = event.target.value;
        if (selected === '') onChange(undefined);
        else {
          const option = options[Number(selected)];
          if (option !== undefined) onChange(option);
        }
      }}>
      <MenuItem value="">{t('superAdminManageVoice.advanced.unset')}</MenuItem>
      {configured && (
        <MenuItem value="json" disabled>
          {configuredLabel}
        </MenuItem>
      )}
      {options.map((option, optionIndex) => (
        <MenuItem key={String(option)} value={String(optionIndex)}>
          {option === ''
            ? t('superAdminManageVoice.advanced.automatic')
            : typeof option === 'boolean'
              ? t(`superAdminManageVoice.advanced.${option}`)
              : String(option)}
        </MenuItem>
      ))}
    </TextField>
  );
};
